/**
 * errors.ts — global error capture for telemetry.
 *
 * Wires three error sources:
 *   1. window.onerror — uncaught synchronous errors
 *   2. window.unhandledrejection — uncaught Promise rejections
 *   3. React error boundary integration (exported trackBoundaryError)
 *
 * Contract (§3, §5, §10):
 *   - Scrubs (truncate-then-redact) before queuing — see scrub.ts.
 *   - Raw pre-scrub values are NEVER logged.
 *   - DNT/GPC honored — no events if opted out.
 *   - Swallows all errors — telemetry must NEVER affect app behavior.
 *   - `errorAppVersion` mirrors the version from the batch envelope.
 *   - De-duplication: a simple 10-item LRU prevents the same error flooding
 *     the queue on tight loops (e.g. an error inside a render that re-fires).
 */

import { isDntActive } from './dnt';
import { enqueueEvent } from './queue';
import { currentRouteTemplate } from './route';
import { scrubError } from './scrub';
import type { ErrorEvent } from './types';

// ---------------------------------------------------------------------------
// App version
// ---------------------------------------------------------------------------

function getAppVersion(): string {
  const v =
    (import.meta.env?.PACKAGE_VERSION as string | undefined) ??
    (import.meta.env?.VITE_BUILD_SHA as string | undefined) ??
    'unknown';
  return v;
}

// ---------------------------------------------------------------------------
// Simple dedup ring — prevent a looping error from flooding the queue.
// Keyed on the first 120 chars of the raw (pre-scrub) message + name combo.
// ---------------------------------------------------------------------------

const DEDUP_MAX = 10;
const _recent: string[] = [];

function isDuplicate(key: string): boolean {
  if (_recent.includes(key)) return true;
  _recent.push(key);
  if (_recent.length > DEDUP_MAX) _recent.shift();
  return false;
}

// ---------------------------------------------------------------------------
// Core helper
// ---------------------------------------------------------------------------

function enqueueError(rawName: string, rawMessage: string, rawStack: string): void {
  try {
    if (isDntActive()) return;

    // Dedup check before scrub (uses raw values as key — never logged)
    const dedupKey = `${rawName}:${rawMessage.slice(0, 120)}`;
    if (isDuplicate(dedupKey)) return;

    // Truncate then scrub — raw values must NOT be logged anywhere after this.
    const scrubbed = scrubError(rawName, rawMessage, rawStack);

    const event: ErrorEvent = {
      type: 'error',
      ts: new Date().toISOString(),
      routeTemplate: currentRouteTemplate(),
      online: typeof navigator !== 'undefined' ? navigator.onLine : true,
      name: scrubbed.name,
      message: scrubbed.message,
      stack: scrubbed.stack,
      errorAppVersion: getAppVersion(),
    };

    void enqueueEvent(event).catch(() => {
      /* swallow */
    });
  } catch {
    // Final safety net — must not throw.
  }
}

// ---------------------------------------------------------------------------
// window.onerror handler
// ---------------------------------------------------------------------------

function handleWindowError(
  event: Event | string,
  _source?: string,
  _lineno?: number,
  _colno?: number,
  error?: Error,
): void {
  const name = error?.name ?? 'Error';
  const message = error?.message ?? (typeof event === 'string' ? event : 'Unknown error');
  const stack = error?.stack ?? '';
  enqueueError(name, message, stack);
}

// ---------------------------------------------------------------------------
// window.unhandledrejection handler
// ---------------------------------------------------------------------------

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason = event.reason as unknown;
  const name = reason instanceof Error ? reason.name : 'UnhandledRejection';
  const message = reason instanceof Error ? reason.message : String(reason).slice(0, 1024);
  const stack = reason instanceof Error ? (reason.stack ?? '') : '';
  enqueueError(name, message, stack);
}

// ---------------------------------------------------------------------------
// React error boundary hook — called from ErrorBoundary.componentDidCatch
// ---------------------------------------------------------------------------

/**
 * Call from componentDidCatch to report React render errors via telemetry.
 * The error boundary still shows its fallback UI — this is fire-and-forget.
 */
export function trackBoundaryError(error: Error): void {
  enqueueError(error.name, error.message, error.stack ?? '');
}

// ---------------------------------------------------------------------------
// Init — wire the global handlers once
// ---------------------------------------------------------------------------

let _initialised = false;

/**
 * Register global error handlers. Safe to call multiple times (no-op after first).
 */
export function initErrorTracking(): void {
  if (_initialised || typeof window === 'undefined') return;
  _initialised = true;

  window.addEventListener('error', handleWindowError as EventListener);
  window.addEventListener('unhandledrejection', handleUnhandledRejection as EventListener);
}
